import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookOpen, X, FileText, CircleAlert, Upload, PenLine, Search, Copy, ArrowUp, Loader, EyeOff } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Segmented, Label11, usd, int, plural } from "@/components/common";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Ask your documents (design_handoff_documents_view/screens/Ask Your Documents.dc.html; docs/PROFESSIONS_PLAN.md 3.4):
// a small chat over the PDFs the professional adds, with Claude Haiku 5.5 or GPT-6 Luna. The PDFs stay on the
// workspace server (backend/ask_routes.py); the chat is the professional's only, read with this unlock's token. It
// never changes the company's files: the professional copies what they need.

export const CHAT_MODELS = [["haiku", "Claude Haiku 5.5"], ["luna", "GPT-6 Luna"]];
const MODEL_LABEL = Object.fromEntries(CHAT_MODELS);
const MAX_MB = 20;  // backend/ask_routes.MAX_UPLOAD
const NOT_PDF = "Only PDF. Word, Pages and Google Docs can save a PDF.";
const MODES = { draft: "Write or rephrase text from your documents.", find: "Get the exact passages, word for word." };

// the session's PDFs; the header counts them too
export function useReferences(sid, token) {
  return useQuery({
    queryKey: ["ask", sid, "references"], enabled: !!token, retry: false, staleTime: 60000,
    queryFn: async () => (await api.get(`/sessions/${sid}/references`, { headers: { "X-Unlock-Token": token } })).data.documents,
  });
}

const size = (bytes) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);
const cost = (n) => (n > 0 && n < 0.01 ? "under $0.01" : usd(n));

async function copy(text, said) {
  try { await navigator.clipboard.writeText(text); toast.success(said); }
  catch { toast.error("Couldn't copy. Select the text and copy it yourself."); }
}

function CopyButton({ text, said, testid }) {
  return <Button variant="outline" size="sm" className="h-7 px-2 shrink-0" onClick={() => copy(text, said)} data-testid={testid}><Copy className="w-3.5 h-3.5" />Copy</Button>;
}

function Answer({ e, i }) {
  const meta = `${e.mode === "find" ? "Find exact text" : "Draft"} · ${MODEL_LABEL[e.model] || e.model}${e.cost_usd ? ` · ${cost(e.cost_usd)}` : ""}`;
  return (
    <div className="flex flex-col gap-2" data-testid={`ask-entry-${i}`}>
      <div className="flex justify-end"><div className="max-w-[90%] bg-card rounded-[10px] px-3 py-2 whitespace-pre-wrap [overflow-wrap:anywhere]">{e.q}</div></div>
      <span className="flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground tabular-nums">
        {e.mode === "find" ? <Search className="w-3 h-3" /> : <PenLine className="w-3 h-3" />}{meta}</span>
      {e.kind === "draft" && (
        <div className="border rounded-lg px-3 py-2.5 flex flex-col gap-2">
          <div className="whitespace-pre-wrap [overflow-wrap:anywhere] [text-wrap:pretty]">{e.text}</div>
          <span className="self-start"><CopyButton text={e.text} said="Draft copied." testid={`ask-copy-draft-${i}`} /></span>
        </div>
      )}
      {e.kind === "quotes" && e.quotes.map((q, j) => (
        <figure key={j} className="m-0 border rounded-lg px-3 py-2.5 flex flex-col gap-2" data-testid={`ask-quote-${i}-${j}`}>
          <blockquote className="m-0 ps-2.5 border-s-2 border-gr-control-border text-foreground whitespace-pre-wrap [overflow-wrap:anywhere] [text-wrap:pretty]">{q.text}</blockquote>
          <figcaption className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
            <span className="[overflow-wrap:anywhere] tabular-nums">{q.doc}{q.page ? ` · page ${q.page}` : ""}</span>
            <CopyButton text={q.text} said="Quote copied." testid={`ask-copy-quote-${i}-${j}`} />
          </figcaption>
        </figure>
      ))}
      {e.kind === "none" && <div className="border rounded-lg px-3 py-2.5 text-muted-foreground">Not found in this document.</div>}
      {e.kind === "error" && (
        <div role="alert" className="border rounded-lg px-3 py-2.5 flex gap-2 text-gr-danger">
          <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" /><span className="[text-wrap:pretty]">{e.err}</span></div>
      )}
      {e.kind === "pending" && (
        <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader className="w-3.5 h-3.5 text-gr-warning motion-safe:animate-spin [animation-duration:2s]" aria-hidden="true" />
          {MODEL_LABEL[e.model]} is reading your PDFs…</div>
      )}
    </div>
  );
}

// `engine`: the session's, which picks the model it starts on; `out`: the session's budget is used up
export default function AskDocuments({ sid, token, engine, out, onClose }) {
  const qc = useQueryClient();
  const refsQ = useReferences(sid, token);
  const historyQ = useQuery({
    queryKey: ["ask", sid, "history"], enabled: !!token, retry: false,
    queryFn: async () => (await api.get(`/sessions/${sid}/ask`, { headers: { "X-Unlock-Token": token } })).data.history,
  });
  const [mode, setMode] = useState("find");
  const [model, setModel] = useState(engine === "codex" ? "luna" : "haiku");
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(null);  // the question being answered
  const [uploads, setUploads] = useState([]);  // {key, name, pct, st: uploading | reading | error, err}
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const docs = refsQ.data || [];
  const history = historyQ.data || [];
  const unavailable = refsQ.error ? apiErr(refsQ.error) : null;  // e.g. the workspace server needs an update
  const canAsk = docs.length > 0 && !out && !pending && !unavailable;

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [history.length, pending]);

  const setUpload = (key, change) => setUploads((all) => all.map((u) => (u.key === key ? { ...u, ...change } : u)));
  const add = async (files) => {
    for (const file of [...files]) {
      const key = `${file.name}:${Date.now()}:${Math.random()}`;
      const looksPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      const err = !looksPdf ? NOT_PDF : file.size > MAX_MB * 1024 * 1024 ? `Over ${MAX_MB} MB. Split it into parts.` : null;
      setUploads((all) => [...all, { key, name: file.name, pct: 0, st: err ? "error" : "uploading", err }]);
      if (err) continue;
      try {
        const { data } = await api.post(`/sessions/${sid}/references`, file, {
          params: { name: file.name }, headers: { "X-Unlock-Token": token, "Content-Type": "application/pdf" },
          onUploadProgress: (p) => {
            const pct = p.total ? Math.round((p.loaded / p.total) * 100) : 0;
            setUpload(key, pct >= 100 ? { st: "reading", pct } : { pct });
          },
        });
        qc.setQueryData(["ask", sid, "references"], (now) => [...(now || []), data.document]);
        setUploads((all) => all.filter((u) => u.key !== key));
      } catch (e) { setUpload(key, { st: "error", err: apiErr(e) }); }
    }
  };
  const remove = async (doc) => {
    try {
      await api.delete(`/sessions/${sid}/references/${doc.id}`, { headers: { "X-Unlock-Token": token } });
      qc.setQueryData(["ask", sid, "references"], (now) => (now || []).filter((d) => d.id !== doc.id));
      toast.success(`${doc.name} removed. The agent can't read it any more.`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const ask = async () => {
    const question = input.trim();
    if (!question || !canAsk) return;
    setPending({ q: question, mode, model, kind: "pending" });
    setInput("");
    try {
      const { data } = await api.post(`/sessions/${sid}/ask`, { question, mode, model }, { headers: { "X-Unlock-Token": token } });
      qc.setQueryData(["ask", sid, "history"], (now) => [...(now || []), data.entry]);
      qc.invalidateQueries({ queryKey: ["session", sid] });  // its spend went up
    } catch (e) { toast.error(apiErr(e)); setInput(question); }
    setPending(null);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  const pages = docs.reduce((a, d) => a + (d.pages || 0), 0);
  const spent = history.reduce((a, h) => a + (h.cost_usd || 0), 0);
  const placeholder = !docs.length ? "Add a PDF to start" : mode === "draft"
    ? "Ask for a draft, for example a sentence for the refund page" : "Name a term or rule, for example the withdrawal period";

  return (
    // the side panel's place on a wide screen, the whole screen on a phone
    <section aria-label="Ask your documents" data-testid="ask-documents"
      onKeyDown={(e) => { if (e.key === "Escape" && !e.defaultPrevented) { e.stopPropagation(); onClose(); } }}
      className="fixed inset-0 z-40 lg:static lg:inset-auto lg:z-auto lg:w-[360px] shrink-0 flex flex-col min-h-0 bg-background lg:border-s">
      <div className="shrink-0 flex items-center justify-between gap-2 ps-4 pe-2 py-2 border-b">
        <span className="flex items-center gap-2 font-display text-[15px] tracking-[0.02em] font-semibold"><BookOpen className="w-4 h-4" />Ask your documents</span>
        <Button size="icon" variant="ghost" onClick={onClose} aria-label="Close Ask your documents" title="Close" data-testid="ask-close"><X /></Button>
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3.5 flex flex-col gap-4">
        <div className="flex flex-col gap-2" data-testid="ask-pdfs">
          <div className="flex justify-between items-baseline gap-2">
            <Label11>Your PDFs</Label11>
            {docs.length > 0 && <span className="text-[11px] leading-4 text-muted-foreground tabular-nums">{plural(docs.length, "PDF")} · {int(pages)} pages</span>}
          </div>
          {unavailable && <div role="alert" className="flex gap-2 text-sm text-gr-danger"><CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="[text-wrap:pretty]">{unavailable}</span></div>}
          {docs.map((d) => (
            <div key={d.id} className="border rounded-lg py-2 ps-2.5 pe-1.5 flex items-start gap-2.5" data-testid={`ask-pdf-${d.id}`}>
              <FileText className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span className="flex-1 min-w-0 flex flex-col"><span className="font-semibold [overflow-wrap:anywhere]">{d.name}</span>
                <span className="text-sm text-muted-foreground tabular-nums">{plural(d.pages, "page")} · {size(d.bytes)}</span></span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => remove(d)}
                aria-label={`Remove ${d.name}`} title="Remove"><X className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          {uploads.map((u) => (
            <div key={u.key} className="border rounded-lg py-2 ps-2.5 pe-1.5 flex flex-col gap-1.5" data-testid="ask-upload">
              <div className="flex items-start gap-2.5">
                {u.st === "error" ? <CircleAlert className="w-4 h-4 mt-0.5 shrink-0 text-gr-danger" /> : <FileText className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />}
                <span className="flex-1 min-w-0 flex flex-col"><span className="font-semibold [overflow-wrap:anywhere]">{u.name}</span>
                  {u.st === "error" ? <span className="text-sm text-gr-danger [text-wrap:pretty]">{u.err}</span>
                    : <span className="text-sm text-muted-foreground tabular-nums">{u.st === "uploading" ? `Uploading… ${u.pct}%` : "Reading the pages…"}</span>}
                </span>
                {u.st === "error" && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" aria-label={`Dismiss ${u.name}`} title="Dismiss"
                    onClick={() => setUploads((all) => all.filter((x) => x.key !== u.key))}><X className="w-3.5 h-3.5" /></Button>
                )}
              </div>
              {u.st === "uploading" && <div className="h-1 me-1 rounded-sm bg-gr-surface-2 overflow-hidden"><div className="h-full bg-foreground" style={{ width: `${u.pct}%` }} /></div>}
            </div>
          ))}
          {!docs.length && !uploads.length && !unavailable && refsQ.isSuccess && (
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">No PDFs yet. Add a law, a contract or a style guide, then ask about it here.</span>
          )}
          <div className="flex flex-col items-start gap-1.5">
            <input ref={fileRef} type="file" accept="application/pdf,.pdf" multiple hidden data-testid="ask-file"
              onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!!unavailable} data-testid="ask-add"><Upload />Add PDFs</Button>
            <span className="text-sm text-muted-foreground">{NOT_PDF}</span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 pt-3.5 border-t">
          <Segmented label="Mode" className="w-full [&>button]:flex-1" value={mode} onChange={setMode} testid="ask-mode"
            options={[["draft", <span key="d" className="inline-flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" />Draft</span>],
              ["find", <span key="f" className="inline-flex items-center gap-1.5"><Search className="w-3.5 h-3.5" />Find exact text</span>]]} />
          <span className="text-sm text-muted-foreground">{MODES[mode]}</span>
        </div>

        {!history.length && !pending && docs.length > 0 && (
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">Ask a question about your PDFs. Your questions and answers stay here when you unlock the session again.</span>
        )}
        {history.map((e, i) => <Answer key={e.id || i} e={e} i={i} />)}
        {pending && <Answer e={pending} i={history.length} />}
      </div>

      <div className="shrink-0 border-t px-4 pt-2.5 pb-3 flex flex-col gap-2">
        {out && (
          <div role="status" className="flex gap-2 text-sm text-gr-danger"><CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span className="[text-wrap:pretty]">This session's budget is used up. Raise it in Session budget to ask more.</span></div>
        )}
        <div className={cn("border border-gr-control-border rounded-lg p-1.5 ps-2.5 flex items-end gap-1.5", (!docs.length || out) && "opacity-40")}>
          <textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown} maxLength={2000}
            disabled={!docs.length || out || !!unavailable} placeholder={placeholder} aria-label="Ask about your PDFs" data-testid="ask-input"
            className="flex-1 min-w-0 bg-transparent resize-none outline-none leading-5 py-0.5 placeholder:text-muted-foreground" />
          <Button size="icon" className="rounded-full shrink-0" onClick={ask} aria-disabled={!canAsk || !input.trim()} aria-label="Ask" title="Ask" data-testid="ask-send">
            <ArrowUp /></Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5">
          <div role="radiogroup" aria-label="Chat model" className="flex items-center gap-1.5" data-testid="ask-model">
            <span className="text-[11px] leading-4 text-muted-foreground">Model</span>
            <div className="flex border rounded-md overflow-hidden">
              {CHAT_MODELS.map(([v, label]) => (
                <button key={v} type="button" role="radio" aria-checked={model === v} onClick={() => setModel(v)}
                  className={cn("h-[22px] px-2 text-[11px] whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                    model === v ? "bg-gr-surface-2 font-semibold" : "text-muted-foreground hover:bg-gr-hover")}>{label}</button>
              ))}
            </div>
          </div>
          {history.length > 0 && <span className="text-[11px] leading-4 text-muted-foreground tabular-nums">This chat: {cost(spent)}, counted in your session budget</span>}
        </div>
        <span className="flex gap-1.5 items-start text-[11px] leading-4 text-muted-foreground">
          <EyeOff className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="[text-wrap:pretty]">Only you can read this chat. The agent can read your PDFs too; they are never added to the company's files.</span>
        </span>
      </div>
    </section>
  );
}
